/* gcpx-unisex: "Ladies & Gentlemen" (sections/gcpx-unisex).
   - Tabs: the glass toggle is an ARIA tablist (click, Left/Right in the reading direction, Home/End).
     Both groups share one grid cell, so a switch never moves the page; the other group is inert.
   - Blur-up: a photo or poster that has not loaded yet is hidden over its blurred 24px copy.
   - Loading: nothing heavy until the section is near the screen (400px); then each video gets
     preload="metadata" and its full-size first-frame poster, and every tile that scrolls into its row
     loads now together with the next and the previous one (images eager, videos preload="auto"), so a
     swipe or a chevron never shows a blank or blurry frame.
   - Videos (the examples-row approach of assets/gcls-set.js): one MP4 up to 1080p, muted, looped,
     playsinline. Every video of the group shown plays on its own while any part of it is on screen
     (owner, 29 Sep 2026: autoplay all the videos in the section) and pauses off-screen, in the other
     group and while the browser tab is hidden. They are silent; the tile stays the pause control.
     No button over the video: the tile is the keyboard control (role=button, Enter/Space), its label
     switching between the Play and Pause settings; a video the shopper paused stays paused.
   - Motion (owner, 29 Sep 2026: the row moved abruptly, the switch was too sudden):
     - The rows are kit glide rows (data-gcx-motion="glide", gcx-kit.js 1d; this row's motion, moved into the kit on
       30 Sep 2026 so the ideas, leather and color rows share the one copy): a mouse drags them 1:1 and they coast to
       a stop, a finger swipes natively with no snapping, the chevrons glide one tile. A drag never plays or pauses a
       video (the kit eats the click that ends it; a press that does not move still does).
     - Tabs: the toggle's glass thumb glides to the tab chosen; the old group fades out with a short slide and a soft
       blur, the new one comes in from the side the thumb moved to, its tiles a beat apart (Web Animations, about
       0.6 s, no layout change: both groups share one grid cell). The old group's videos pause once it has faded;
       the new group's videos start at once. prefers-reduced-motion: a quick fade only, no glide or inertia. */
(function () {
  'use strict';
  var secs = document.querySelectorAll('[data-gcpx-ux]');
  if (!secs.length) return;
  var mq = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
  var hasIO = 'IntersectionObserver' in window;
  function arr(l) { return Array.prototype.slice.call(l); }
  function onChange(m, fn) { if (!m) return; if (m.addEventListener) m.addEventListener('change', fn); else if (m.addListener) m.addListener(fn); }

  function warm(v) {
    if (!v || v.__ux_warm) return;
    v.__ux_warm = 1;
    var p = v.getAttribute('data-poster');
    if (p && !v.getAttribute('poster')) v.setAttribute('poster', p);
    if (v.preload === 'none') v.preload = 'metadata';
  }
  function eager(tile) {
    if (!tile) return;
    arr(tile.querySelectorAll('img[loading="lazy"]')).forEach(function (im) { im.loading = 'eager'; });
    arr(tile.querySelectorAll('video')).forEach(function (v) { warm(v); if (v.preload !== 'auto') v.preload = 'auto'; });
  }
  function blurUp(fig) {
    var im = fig.querySelector('img');
    if (!im || (im.complete && im.naturalWidth)) return;
    fig.classList.add('is-loading');
    function done() { fig.classList.remove('is-loading'); }
    im.addEventListener('load', done, { once: true });
    im.addEventListener('error', done, { once: true });
  }

  function reduced() { return !!(mq && mq.matches); }
  var EASE = 'cubic-bezier(.22,.61,.36,1)';

  function setup(sec) {
    var players = [];
    function syncAll() { players.forEach(function (p) { p(); }); }
    /* a group plays while it is shown, and while it is still fading out after a switch */
    function panelOn(el) { var p = el.closest('[data-gcpx-ux-panel]'); return !p || p.classList.contains('is-on') || !!p.__uxLeaving; }

    /* ---- videos ---- */
    function setupVideo(fig) {
      var v = fig.querySelector('video[data-gcpx-ux-video]');
      if (!v || v.__ux) return;
      v.__ux = 1;
      /* role=button, tabindex and the describedby link are in the markup (snippets/gcpx-unisex-tile) */
      fig.addEventListener('keydown', function (e) { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); fig.click(); } });
      var visible = false, userPlay = false, userPause = false, refused = false;
      v.muted = true;
      function playing() { return !v.paused && !v.ended; }
      function want() { return visible && panelOn(fig) && !document.hidden && (userPlay || (!userPause && !refused)); }
      function ui() {
        var on = playing();
        fig.classList.toggle('is-playing', on);
        var l = fig.getAttribute(on ? 'data-pause' : 'data-play') || '';
        if (fig.getAttribute('aria-label') !== l) fig.setAttribute('aria-label', l);
      }
      function play() {
        warm(v);
        var p = null;
        try { p = v.play(); } catch (e) {}
        /* only a refusal (autoplay blocked, e.g. Low Power Mode) turns autoplay off; a play() cut short by our
           own pause() (AbortError: scrolled away, other group) does not */
        if (p && p.catch) p.catch(function (err) { if (!userPlay && err && err.name === 'NotAllowedError') refused = true; ui(); });
      }
      function sync() {
        if (want()) { if (!playing()) play(); }
        else if (playing()) v.pause();
        ui();
      }
      fig.addEventListener('click', function (e) {
        e.preventDefault();
        if (playing()) { userPause = true; userPlay = false; v.pause(); }
        else { userPlay = true; userPause = false; refused = false; play(); }
        ui();
      });
      ['play', 'playing', 'pause'].forEach(function (t) { v.addEventListener(t, ui); });
      document.addEventListener('visibilitychange', sync);
      onChange(mq, sync);
      if (hasIO) {
        new IntersectionObserver(function (ents) {
          ents.forEach(function (en) {
            /* any part on screen (the row's own scroll clips count too) */
            visible = en.isIntersecting && en.intersectionRatio > 0;
          });
          sync();
        }, { threshold: [0, 0.01] }).observe(v);
      }
      players.push(sync);
      ui();
    }

    /* ---- loading ---- */
    function setupPreload(row, track) {
      var tiles = arr(track.children);
      if (!hasIO) { tiles.slice(0, 2).forEach(eager); return; }
      var io = new IntersectionObserver(function (ents) {
        ents.forEach(function (en) {
          if (!en.isIntersecting) return;
          eager(en.target);
          eager(en.target.nextElementSibling);
          eager(en.target.previousElementSibling);
        });
      }, { root: track, threshold: 0.01 });
      tiles.forEach(function (t) { io.observe(t); });
    }

    arr(sec.querySelectorAll('.gcpx-ux__fig')).forEach(blurUp);
    arr(sec.querySelectorAll('[data-gcpx-ux-vid]')).forEach(setupVideo);
    var rows = arr(sec.querySelectorAll('[data-gcx-scroll]'));
    function start() {
      arr(sec.querySelectorAll('video[data-gcpx-ux-video]')).forEach(warm);
      rows.forEach(function (row) { var tr = row.querySelector('[data-gcx-track]'); if (tr) setupPreload(row, tr); });
    }
    if (hasIO) {
      var outer = new IntersectionObserver(function (ents) {
        if (!ents.some(function (en) { return en.isIntersecting; })) return;
        outer.disconnect();
        start();
      }, { rootMargin: '400px 0px' });
      outer.observe(sec);
    } else start();

    /* ---- tabs ---- */
    var tabs = arr(sec.querySelectorAll('[data-gcpx-ux-tab]'));
    if (tabs.length < 2) return;
    var cur = 0;
    tabs.forEach(function (t, i) { if (t.getAttribute('aria-selected') === 'true') cur = i; });
    function panelOf(i) { return document.getElementById(tabs[i].getAttribute('aria-controls')); }

    /* the glass thumb under the selected tab (CSS .is-glide draws the selected look on it instead of on the tab) */
    var toggle = tabs[0].parentNode, thumb = toggle.querySelector('.gcpx-ux__thumb');
    function place(i, live) {
      if (!thumb) return;
      var t = tabs[i];
      if (!live) toggle.classList.remove('is-live');
      toggle.style.setProperty('--thumb-x', t.offsetLeft + 'px');
      toggle.style.setProperty('--thumb-y', t.offsetTop + 'px');
      toggle.style.setProperty('--thumb-w', t.offsetWidth + 'px');
      toggle.style.setProperty('--thumb-h', t.offsetHeight + 'px');
      toggle.classList.add('is-glide');
      if (!live) { void toggle.offsetWidth; requestAnimationFrame(function () { toggle.classList.add('is-live'); }); }
    }
    if (thumb) {
      place(cur, false);
      if ('ResizeObserver' in window) new ResizeObserver(function () { place(cur, false); }).observe(toggle);
      else window.addEventListener('resize', function () { place(cur, false); });
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(function () { place(cur, false); });
    }

    /* the switch (Web Animations; without them, the CSS fade) */
    var canAnim = typeof Element.prototype.animate === 'function';
    if (canAnim) sec.classList.add('is-waapi');
    function now(el) {
      var cs = getComputedStyle(el);
      return { opacity: cs.opacity, transform: cs.transform === 'none' ? 'translate3d(0px,0px,0px)' : cs.transform, filter: cs.filter === 'none' ? 'blur(0px)' : cs.filter };
    }
    function kill(el) { (el.__uxA || []).forEach(function (a) { try { a.cancel(); } catch (e) {} }); el.__uxA = []; }
    function busy(el) { return (el.__uxA || []).some(function (a) { return a.playState === 'running'; }); }
    function run(el, kf, o) { var a = el.animate(kf, o); (el.__uxA = el.__uxA || []).push(a); return a; }
    /* o / f: each group's look just before the switch (read before the classes change) */
    function swap(pOut, pIn, s, o, f) {
      var rm = reduced();
      /* the old group: from wherever it is now (a switch back mid-way reverses smoothly) */
      kill(pOut);
      pOut.__uxLeaving = true;
      var out = run(pOut, [
        { opacity: o.opacity, transform: o.transform, filter: o.filter, visibility: 'visible' },
        rm ? { opacity: 0, transform: o.transform, filter: o.filter, visibility: 'visible' }
           : { opacity: 0, transform: 'translate3d(' + (-s * 20) + 'px,0px,0px)', filter: 'blur(4px)', visibility: 'visible' }
      ], { duration: rm ? 140 : 380, easing: 'cubic-bezier(.4,0,.2,1)' });
      out.onfinish = function () { pOut.__uxLeaving = false; pOut.__uxA = []; syncAll(); };
      /* the new group: from the side the thumb moved to, or from where it was if it was still leaving */
      var back = !!f;
      kill(pIn);
      pIn.__uxLeaving = false;
      var from = f || (rm ? { opacity: 0, transform: 'translate3d(0px,0px,0px)', filter: 'blur(0px)' }
                          : { opacity: 0, transform: 'translate3d(' + (s * 12) + 'px,0px,0px)', filter: 'blur(8px)' });
      run(pIn, [
        { opacity: from.opacity, transform: from.transform, filter: from.filter },
        { opacity: 1, transform: 'translate3d(0px,0px,0px)', filter: 'blur(0px)' }
      ], { duration: rm ? 180 : 560, delay: back || rm ? 0 : 90, easing: EASE, fill: 'backwards' });
      if (rm || back) return;
      /* its tiles in view come in a beat apart, the one nearest the side they come from first */
      var tr = pIn.querySelector('[data-gcx-track]');
      if (!tr) return;
      var box = tr.getBoundingClientRect(), seen = [];
      arr(tr.children).forEach(function (it) { var r = it.getBoundingClientRect(); if (r.width && r.right > box.left + 1 && r.left < box.right - 1) seen.push([r.left, it]); });
      seen.sort(function (a, b) { return s > 0 ? a[0] - b[0] : b[0] - a[0]; });
      seen.forEach(function (x, i) {
        kill(x[1]);
        run(x[1], [{ transform: 'translate3d(' + (s * (18 + 8 * i)) + 'px,0px,0px)' }, { transform: 'translate3d(0px,0px,0px)' }],
          { duration: 620, delay: 90 + 45 * i, easing: EASE, fill: 'backwards' });
      });
    }
    function select(i, focus) {
      if (i === cur) { if (focus) tabs[i].focus(); return; }
      var old = cur;
      cur = i;
      var pIn = panelOf(i), pOut = panelOf(old);
      var o = canAnim && pOut ? now(pOut) : null, f = canAnim && pIn && busy(pIn) ? now(pIn) : null;
      tabs.forEach(function (t, j) {
        var on = j === i;
        t.setAttribute('aria-selected', on ? 'true' : 'false');
        t.tabIndex = on ? 0 : -1;
        var p = panelOf(j);
        if (!p) return;
        p.classList.toggle('is-on', on);
        if (on) p.removeAttribute('inert'); else p.setAttribute('inert', '');
      });
      /* the content comes in from the side the thumb moves to */
      var s = tabs[i].getBoundingClientRect().left > tabs[old].getBoundingClientRect().left ? 1 : -1;
      place(i, true);
      if (focus) tabs[i].focus();
      if (canAnim && pIn && pOut) swap(pOut, pIn, s, o, f);
      var row = pIn && pIn.querySelector('[data-gcx-scroll]');
      if (row && row.__gcxRow) row.__gcxRow.update();
      syncAll();
    }
    tabs.forEach(function (t, i) {
      t.addEventListener('click', function () { select(i, false); });
      t.addEventListener('keydown', function (e) {
        var rtl = getComputedStyle(t).direction === 'rtl';
        var n = tabs.length, k = e.key, to = -1;
        if (k === 'ArrowRight') to = rtl ? i - 1 : i + 1;
        else if (k === 'ArrowLeft') to = rtl ? i + 1 : i - 1;
        else if (k === 'Home') to = 0;
        else if (k === 'End') to = n - 1;
        else return;
        e.preventDefault();
        select((to + n) % n, true);
      });
    });
  }

  arr(secs).forEach(setup);
})();
